import { Sparkles } from 'lucide-react'
import { releaseNotes, releaseDate, type ReleaseNote } from '../data/releaseNotes'
import './ReleaseNotes.css'

// Public-facing release notes. The list is curated in src/data/releaseNotes.ts (the customer-safe
// view of what shipped) and is already sorted newest-first there; we render it as-is. Headline
// 'milestone' releases render as full cards; smaller 'minor' updates render as lighter, compact
// cards with a brand accent so the two read differently while both stay clearly visible in the
// list. Visible to all signed-in users — see the audience note in releaseNotes.ts.

function formatDate(iso: string): string {
  // Parse as a local date (avoid the UTC shift `new Date('YYYY-MM-DD')` introduces).
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function MilestoneItem({ note }: { note: ReleaseNote }) {
  // Date shown is the note's own deploy date (its single source of truth — see releaseDate).
  const date = releaseDate(note)
  return (
    <li className="release-notes__item" data-testid="release-notes-item" data-kind="milestone">
      <time className="release-notes__date" dateTime={date}>
        {formatDate(date)}
      </time>
      <h2 className="release-notes__item-title">{note.title}</h2>
      <p className="release-notes__item-summary">{note.summary}</p>
    </li>
  )
}

function MinorItem({ note }: { note: ReleaseNote }) {
  // Date shown is the note's own deploy date (its single source of truth — see releaseDate).
  const date = releaseDate(note)
  return (
    <li className="release-notes__minor" data-testid="release-notes-item" data-kind="minor">
      <span className="release-notes__minor-marker" aria-hidden />
      <div className="release-notes__minor-body">
        <div className="release-notes__minor-head">
          <span className="release-notes__minor-tag">Minor update</span>
          <time className="release-notes__minor-date" dateTime={date}>
            {formatDate(date)}
          </time>
        </div>
        <h2 className="release-notes__minor-title">{note.title}</h2>
        <p className="release-notes__minor-summary">{note.summary}</p>
      </div>
    </li>
  )
}

export default function ReleaseNotes() {
  return (
    <div className="release-notes" data-testid="release-notes-page">
      <header className="release-notes__header">
        <h1 className="release-notes__title">
          <Sparkles size={22} aria-hidden />
          Release Notes
        </h1>
        <p className="release-notes__subtitle">
          What’s new in Replay QA — headline features plus the smaller improvements we ship in between.
        </p>
      </header>

      {releaseNotes.length === 0 ? (
        <p className="release-notes__empty" data-testid="release-notes-empty">
          No release notes yet — check back soon.
        </p>
      ) : (
        <ol className="release-notes__list" data-testid="release-notes-list">
          {releaseNotes.map((note, i) =>
            note.kind === 'minor' ? (
              <MinorItem note={note} key={`${releaseDate(note)}-${i}`} />
            ) : (
              <MilestoneItem note={note} key={`${releaseDate(note)}-${i}`} />
            )
          )}
        </ol>
      )}
    </div>
  )
}
